import { useEffect, useState } from "react";
import { Download, FileCode2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { BriefEmailStatus } from "@/components/BriefEmailStatus";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
const KINDS = [["pdf", "PDF guide"], ["md", "Markdown guide"], ["html", "HTML demo"]];

export default function AdminSidebarGuide() {
  const { session } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/admin/briefs/sidebar-guide").then(({ data }) => setM(data)).catch((x) => setErr(errMsg(x))); }, []);
  if (session?.user?.platform_role !== "super_admin") return <p className="text-q-muted" data-testid="sidebar-guide-forbidden">Only the super admin can open the briefs.</p>;
  if (err) return <p className="text-red-300" role="alert" data-testid="sidebar-guide-error">{err}</p>;
  if (!m) return <Loader />;
  const download = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.get(`/admin/briefs/sidebar-guide/download/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = m.files[kind].name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(null); }
  };
  return (
    <div data-testid="sidebar-guide-page">
      <PageHeader eyebrow="Admin · Briefs" title="Sidebar code guide" rgb="var(--glow-admin)" testid="sidebar-guide-title" />
      <section className="q-card p-5">
        <div className="flex items-start gap-4">
          <FileCode2 className="mt-1 h-8 w-8 shrink-0 text-violet-300" />
          <div className="min-w-0">
            <p className="font-semibold text-q-text">{m.title}</p>
            <p className="text-sm text-q-muted">Design tokens, the React source, a minimal example and a single-file HTML version of the web sidebar and hover flyout, for AI agents in other apps.</p>
            <p className="mt-2 text-sm text-q-faint" data-testid="sidebar-guide-meta">
              {m.ready ? `Stored ${when(m.generated_at)} · PDF ${m.pages} pages · ${KINDS.map(([k, l]) => `${l} ${kb(m.files[k].size)}`).join(" · ")}` : "Not stored yet."}
            </p>
            <BriefEmailStatus e={m.email} onDownload={() => download("pdf")} prefix="sidebar-guide" />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {KINDS.map(([k, l], i) => (
            <button key={k} className={`q-btn ${i ? "q-btn-ghost" : "q-btn-primary"} min-h-[44px]`} onClick={() => download(k)} disabled={!m.ready || !!busy} data-testid={`sidebar-guide-download-${k}-btn`}>
              {busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {l}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
